import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ShieldCheckIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import Container from './Container';
import TierBadge from '../TierBadge';
import Badge from '../ui/Badge';
import SiteLogo from './SiteLogo';
import { useAppContext } from '../../context/AppContext';
import { authHelpers } from '../../config/supabaseClient';
import { toast } from 'react-hot-toast';

const GOLD = '#A08D45';
const GOLD_HOVER = '#8a7938';
const NAVY = '#1c3244';

const MARKETING_ANCHORS = [
  { name: 'Benefits', hash: '#benefits' },
  { name: 'Features', hash: '#features' },
  { name: 'How it works', hash: '#how-it-works' },
  { name: 'About Us', path: '/about' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriptionTier, isAdmin, userInfo } = useAppContext();

  const isLanding = location.pathname === '/';

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  const checkAuth = async () => {
    try {
      const user = await authHelpers.getUser();
      setIsAuthenticated(!!user);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await authHelpers.signOut();
      setIsAuthenticated(false);
      setIsUserMenuOpen(false);
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.relative')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const scrollToHash = (hash) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/${hash}`);
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  const showMarketingNav = !isAuthenticated;

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 transition-all duration-300',
        showMarketingNav
          ? isScrolled
            ? 'shadow-md'
            : ''
          : isScrolled
            ? 'bg-white shadow-sm border-b border-neutral-100'
            : 'bg-white border-b border-neutral-100'
      )}
      style={showMarketingNav ? { backgroundColor: NAVY } : undefined}
    >
      <Container>
        <div className="relative flex items-center justify-between h-24">
          <Link
            to="/"
            className="flex items-center z-10"
            onClick={() => {
              if (isLanding) window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <SiteLogo
              imgClassName={showMarketingNav ? 'h-12 w-12 sm:h-14 sm:w-14' : 'h-11 w-11'}
            />
          </Link>

          {/* Desktop Navigation */}
          {showMarketingNav ? (
            <>
              <nav className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-8">
                {MARKETING_ANCHORS.map((item) =>
                  item.path ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="text-xs font-medium tracking-[0.15em] uppercase transition-colors duration-200 text-white/75 hover:text-[#F9EFDD]"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      key={item.hash}
                      type="button"
                      onClick={() => scrollToHash(item.hash)}
                      className="text-xs font-medium tracking-[0.15em] uppercase transition-colors duration-200 text-white/75 hover:text-[#F9EFDD]"
                      style={{ fontFamily: "'Open Sans', sans-serif" }}
                    >
                      {item.name}
                    </button>
                  )
                )}
              </nav>
              <div className="hidden md:flex items-center gap-6 z-10">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-xs font-medium tracking-[0.15em] uppercase transition-colors duration-200 text-white/75 hover:text-[#F9EFDD]"
                  style={{ fontFamily: "'Open Sans', sans-serif" }}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="px-5 py-2.5 text-sm font-button font-medium uppercase tracking-wide text-white border transition-colors duration-200"
                  style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = GOLD_HOVER;
                    e.currentTarget.style.borderColor = GOLD_HOVER;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = GOLD;
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                >
                  Start Free
                </button>
              </div>
            </>
          ) : (
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to="/dashboard"
                className={cn(
                  'text-sm font-medium tracking-luxury uppercase transition-colors duration-200',
                  location.pathname === '/dashboard'
                    ? 'text-brand-blue'
                    : 'text-neutral-600 hover:text-brand-blue'
                )}
              >
                My Collection
              </Link>
              <Link
                to="/template-selection"
                className="px-4 py-2 text-sm font-button font-medium uppercase tracking-wide text-white border transition-colors duration-200"
                style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = GOLD_HOVER;
                  e.currentTarget.style.borderColor = GOLD_HOVER;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = GOLD;
                  e.currentTarget.style.borderColor = GOLD;
                }}
              >
                Create Legacy Video
              </Link>
              <Link
                to="/greeting/template-selection"
                className={cn(
                  'text-sm font-medium tracking-luxury uppercase transition-colors duration-200',
                  location.pathname.startsWith('/greeting')
                    ? 'text-brand-blue'
                    : 'text-neutral-600 hover:text-brand-blue'
                )}
              >
                Send Greeting Video
              </Link>

              <TierBadge tier={subscriptionTier} size="sm" />

              {subscriptionTier === 'free' && (
                <button
                  type="button"
                  onClick={() => navigate('/subscription')}
                  className="px-5 py-2 text-sm font-button font-medium text-white tracking-wide uppercase border transition-colors duration-200"
                  style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = GOLD_HOVER;
                    e.currentTarget.style.borderColor = GOLD_HOVER;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = GOLD;
                    e.currentTarget.style.borderColor = GOLD;
                  }}
                >
                  Upgrade
                </button>
              )}

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 p-2 hover:bg-neutral-100 transition-colors"
                  style={{ borderRadius: 0 }}
                >
                  {isAdmin && (
                    <Badge variant="warning" className="text-xs font-bold">
                      ADMIN
                    </Badge>
                  )}
                  <span className="text-sm font-medium text-neutral-700">
                    {userInfo?.fullName?.split(' ')[0] || 'Account'}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-neutral-600" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg border border-neutral-200 py-2 z-50" style={{ borderRadius: 0 }}>
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            navigate('/admin');
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
                        >
                          <ShieldCheckIcon className="h-4 w-4" />
                          Admin Panel
                        </button>
                        <div className="h-px bg-neutral-200 my-2" />
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        navigate('/dashboard');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      My Collection
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        navigate('/subscription');
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      Manage Subscription
                    </button>

                    <div className="h-px bg-neutral-200 my-2" />

                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </nav>
          )}

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              'md:hidden p-3 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center z-10',
              showMarketingNav
                ? 'text-[#F9EFDD] hover:text-white'
                : 'text-neutral-600 hover:text-neutral-900'
            )}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div
            className={cn(
              'md:hidden py-6 border-t',
              showMarketingNav ? 'border-white/15' : 'border-neutral-100'
            )}
          >
            <nav className="flex flex-col space-y-4">
              {showMarketingNav ? (
                <>
                  {MARKETING_ANCHORS.map((item) =>
                    item.path ? (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-xs font-medium tracking-[0.15em] uppercase text-white/80 hover:text-[#F9EFDD] transition-colors py-2 text-center"
                        style={{ fontFamily: "'Open Sans', sans-serif" }}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <button
                        key={item.hash}
                        type="button"
                        onClick={() => scrollToHash(item.hash)}
                        className="text-xs font-medium tracking-[0.15em] uppercase text-white/80 hover:text-[#F9EFDD] transition-colors py-2 text-center"
                        style={{ fontFamily: "'Open Sans', sans-serif" }}
                      >
                        {item.name}
                      </button>
                    )
                  )}
                  <div className="h-px bg-white/15" />
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/login');
                    }}
                    className="text-xs font-medium tracking-[0.15em] uppercase text-white/80 hover:text-[#F9EFDD] transition-colors py-2 text-center"
                    style={{ fontFamily: "'Open Sans', sans-serif" }}
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/register');
                    }}
                    className="w-full py-3 text-sm font-button font-medium uppercase tracking-wide text-white border"
                    style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                  >
                    Start Free
                  </button>
                </>
              ) : (
                <>
                  <div className="flex justify-center gap-2 mb-2">
                    <TierBadge tier={subscriptionTier} size="md" />
                    {isAdmin && (
                      <Badge variant="warning" className="text-xs font-bold">
                        ADMIN
                      </Badge>
                    )}
                  </div>

                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'text-sm font-medium tracking-luxury uppercase transition-colors duration-200 py-2 text-center',
                      location.pathname === '/dashboard'
                        ? 'text-brand-blue'
                        : 'text-neutral-600 hover:text-brand-blue'
                    )}
                  >
                    My Collection
                  </Link>
                  <Link
                    to="/template-selection"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full py-3 text-sm font-button font-medium uppercase tracking-wide text-white border text-center"
                    style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                  >
                    Create Legacy Video
                  </Link>
                  <Link
                    to="/greeting/template-selection"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'text-sm font-medium tracking-luxury uppercase transition-colors duration-200 py-2 text-center',
                      location.pathname.startsWith('/greeting')
                        ? 'text-brand-blue'
                        : 'text-neutral-600 hover:text-brand-blue'
                    )}
                  >
                    Send Greeting Video
                  </Link>

                  <div className="h-px bg-neutral-200" />

                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className="text-sm font-medium tracking-luxury uppercase text-neutral-600 hover:text-neutral-900 transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                      <ShieldCheckIcon className="h-4 w-4" />
                      Admin Panel
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      handleSignOut();
                      setIsMobileMenuOpen(false);
                    }}
                    className="text-sm font-medium tracking-luxury uppercase text-red-600 hover:text-red-700 transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Sign Out
                  </button>

                  {subscriptionTier === 'free' && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        navigate('/subscription');
                      }}
                      className="w-full py-3 text-sm font-button font-medium uppercase tracking-wide text-white border"
                      style={{ backgroundColor: GOLD, borderColor: GOLD, borderRadius: 0 }}
                    >
                      Upgrade to Premium
                    </button>
                  )}
                </>
              )}
            </nav>
          </div>
        )}
      </Container>
    </header>
  );
};

export default Header;
